const DEFAULT_TIMEOUT_MS = 1500
const DEFAULT_INIT_PATH = '/rest/csrf-token/init'

const pendingRequests = {}

const normalizeHost = passportAPIServerHost => {
  const fallbackHost = typeof window !== 'undefined' ? window.location.origin : ''
  return (passportAPIServerHost || fallbackHost || '').replace(/\/$/, '')
}

const buildInitUrl = ({
  passportAPIServerHost,
  path = DEFAULT_INIT_PATH,
  clientId,
  redirectUri,
}) => {
  const url = new URL(`${normalizeHost(passportAPIServerHost)}${path}`)

  clientId && url.searchParams.append('client_id', clientId)
  redirectUri && url.searchParams.append('redirect_uri', redirectUri)

  return url
}

const withTimeout = ({ promise, timeoutMs, cancel }) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => {
    cancel && cancel()
    reject(new Error('csrf_init_timeout'))
  }, timeoutMs)

  promise
    .then(resolve)
    .catch(reject)
    .finally(() => clearTimeout(timer))
})

const fetchCsrf = ({
  url,
  timeoutMs,
}) => {
  const canAbort = typeof AbortController !== 'undefined'
  const controller = canAbort ? new AbortController() : null

  const requestPromise = fetch(url.href, {
    method: 'GET',
    credentials: 'include',
    signal: controller ? controller.signal : undefined,
  }).then(response => {
    if (!response.ok) {
      throw new Error(`csrf_init_failed_${response.status}`)
    }

    return {
      ok: true,
      status: response.status,
      url: url.href,
    }
  })

  return withTimeout({
    promise: requestPromise,
    timeoutMs,
    cancel: controller ? () => controller.abort() : undefined,
  })
}

const toFailureResult = ({ error, url }) => ({
  ok: false,
  error,
  url,
})

export const initCsrf = ({
  passportAPIServerHost,
  clientId,
  redirectUri,
  path,
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) => {
  const url = buildInitUrl({
    passportAPIServerHost,
    path,
    clientId,
    redirectUri,
  })
  const dedupeKey = url.href

  if (!pendingRequests[dedupeKey]) {
    pendingRequests[dedupeKey] = fetchCsrf({ url, timeoutMs })
      .catch(error => {
        return toFailureResult({ error, url: url.href })
      })
      .finally(() => {
        delete pendingRequests[dedupeKey]
      })
  }

  return pendingRequests[dedupeKey]
}

export const __TEST_ONLY__ = {
  buildInitUrl,
  pendingRequests,
}
